<template>
  <div>
    <div>
      <v-layout class="rounded rounded-md">
        <v-app-bar color="#6b4da9" :elevation="2" rounded>
          <v-icon class="ml-3" size="125">
            <halionSvg></halionSvg>
          </v-icon>
          <v-spacer></v-spacer>
          <span class="mx-2" v-if="isAuthenticated">{{ accInfo.name }}</span>
          <v-menu v-if="isAuthenticated" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn color="#6b4da9" v-bind="props" icon variant="flat">
                <v-icon icon="mdi-account"></v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item prepend-icon="mdi-account">
                {{ username }}
              </v-list-item>
              <v-list-item
                :prepend-icon="theme.name.value === 'system' ? 'mdi-laptop' : theme.name.value === 'light' ? 'mdi-weather-sunny' : 'mdi-weather-night'"
                @click="cycleTheme"
              >
                Theme: {{ themeText }}
              </v-list-item>
              <v-list-item prepend-icon="mdi-cog" @click="openProfile">
                {{ t('button.editProfile') }}
              </v-list-item>
              <v-list-item prepend-icon="mdi-logout" @click="LogoutFunction">
                <v-list-item-title>{{ t('button.logout') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-app-bar>
        <v-footer class="d-flex align-center justify-center" color="#3A3A3A" app>
          <a class="footer-link" style="height: 5vh; align-content: center" href="https://halion.info/impressum">{{ t('legalNotice') }}</a>
        </v-footer>
        <v-navigation-drawer v-if="!isPending" :width="225" permanent>
          <v-list density="compact" nav prepend-gap="12">
            <template v-for="navigation in availableNavList" :key="navigation.text">
              <v-list-item v-if="navigation.route" class="custom-list-item" exact router :to="navigation.route" :prepend-icon="navigation.icon" :title="t(navigation.text)">
              </v-list-item>
              <v-list-group v-else-if="navigation.submenu && navigation.submenu.length > 0" :value="t(navigation.text)" no-action>
                <template v-slot:activator="{ props }">
                  <v-list-item v-bind="props" :prepend-icon="navigation.icon" :title="t(navigation.text)"></v-list-item>
                </template>
                <div v-for="(sub, i) in navigation.submenu" :key="i">
                  <v-list-item
                    v-if="sub.isVisible || sub.isVisible === undefined"
                    class="custom-list-item"
                    exact
                    router
                    :to="sub.route"
                    prepend-gap="2"
                    prepend-icon="mdi-square-small"
                  >
                    <v-list-item-title>{{ t(sub.text) }}</v-list-item-title>
                  </v-list-item>
                </div>
              </v-list-group>
            </template>
          </v-list>
        </v-navigation-drawer>

        <v-main v-if="!isPending" style="min-height: 300px">
          <v-card class="ma-4" elevation="4" rounded="lg">
            <router-view />
          </v-card>
        </v-main>
      </v-layout>
    </div>
    <Notification :notification="notification" :toggleNotification="toggleNotification" />
    <v-snackbar v-model="snackbar" :timeout="8000">
      <template v-slot:actions>
        <v-btn icon="mdi-close" variant="text" size="small" class="position-absolute top-0 right-0 ma-1" @click="snackbar = false" />
      </template>
      <h2 class="mt-n1">{{ t('title.cookies') }}</h2>
      <p class="mt-5">{{ t('cookieBanner') }}</p>
      <p class="mt-3">
        {{ t('privacyStatement') }}
        <a class="custom-link" href="https://halion.info/datenschutzerklaerung/">{{ t('privacyPolicy') }}</a>
      </p>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { useKeycloak } from '@josempgon/vue-keycloak';
import menu from '@/navigation.js';
import Notification from '@/components/Notification.vue';
import useNotification from '@/hooks/useNotification.js';
import halionSvg from '@/assets/halion_Logo.svg';
import accInfoApi from '@/apis/accountinfo-api';
import terminalSystemApi from '@/apis/terminal-api';
import { storeTerminalSystems } from '@/store/terminal-navbar';

/** @typedef {import('@/types/terminal-system').TerminalSystem} TerminalSystem */

const { username, roles, decodedToken, keycloak, isPending, isAuthenticated } = useKeycloak();
const { t } = useI18n();
const theme = useTheme();
const { notification, toggleNotification } = useNotification();
/** @type {import('vue').Ref<{ name: string }>} */
const accInfo = ref({ name: '' });

const snackbar = ref(false);
const themeSetting = ref(localStorage.getItem('theme-pref') || 'system');

const availableNavList = computed(() => {
  storeTerminalSystems.nav = menu.find((x) => x.text === 'navigation.masterdata') ?? { icon: '', text: '' };
  setTerminalNavbar();
  if (roles.value.includes('clockitadmin')) return menu;
  else if (roles.value.includes('webuser')) {
    let webuserMenu = reactive(JSON.parse(JSON.stringify(menu.filter((x) => x.route !== '/bookings' && x.route !== '/employees'))));

    for (const clonedGroup of webuserMenu) {
      if (clonedGroup.submenu) clonedGroup.submenu = [];
    }

    if (decodedToken.value.webuser_employees) {
      const menuItem = menu.find((x) => x.route === '/employees') ?? {
        icon: '',
        text: '',
      };
      webuserMenu.splice(1, 0, menuItem);
    }
    if (decodedToken.value.webuser_bookings) {
      const menuItem = menu.find((x) => x.route === '/bookings') ?? {
        icon: '',
        text: '',
      };
      webuserMenu.splice(1, 0, menuItem);
    }
    if (decodedToken.value.webuser_settings) {
      const menuGroup = menu.find((x) => x.text === 'navigation.basicSettings');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.basicSettings');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/settings');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_users) {
      const menuGroup = menu.find((x) => x.text === 'navigation.clockitgo');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.clockitgo');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/appuser');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_workflow) {
      const menuGroup = menu.find((x) => x.text === 'navigation.other');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.other');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/workflows');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_terminals) {
      const menuGroup = menu.find((x) => x.text === 'navigation.masterdata');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.masterdata');

      let foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/devicetypes');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/groups');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/all');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/cti');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/datafox');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/dormakaba');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/intuscom');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/multicom');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/reinersct');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
      foundMenu = menuGroup?.submenu?.find((x) => x.route === '/terminal/suprema');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);

      const menuGroupBs = menu.find((x) => x.text === 'navigation.basicSettings');
      const clonedGroupBs = webuserMenu.find((x) => x.text === 'navigation.basicSettings');
      foundMenu = menuGroupBs?.submenu?.find((x) => x.route === '/terminal/systems');
      if (foundMenu) clonedGroupBs?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_badge) {
      const menuGroup = menu.find((x) => x.text === 'navigation.masterdata');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.masterdata');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/badges');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_permissions) {
      const menuGroup = menu.find((x) => x.text === 'navigation.permissions');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.permissions');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/usergroups');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_geofences) {
      const menuGroup = menu.find((x) => x.text === 'navigation.clockitgo');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.clockitgo');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/geofences');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_geofences && decodedToken.value.webuser_users) {
      const menuGroup = menu.find((x) => x.text === 'navigation.clockitgo');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.clockitgo');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/appuser-geofences');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_breakmodels) {
      const menuGroup = menu.find((x) => x.text === 'navigation.masterdata');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.masterdata');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/breaks');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_timeframes) {
      const menuGroup = menu.find((x) => x.text === 'navigation.masterdata');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.masterdata');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/timeframes');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }
    if (decodedToken.value.webuser_accessprotocol) {
      const menuGroup = menu.find((x) => x.text === 'navigation.other');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.other');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/accesslog');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }

    if (decodedToken.value.webuser_logs) {
      const menuGroup = menu.find((x) => x.text === 'navigation.other');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.other');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/log');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }

    if (decodedToken.value.webuser_pushmessages) {
      const menuGroup = menu.find((x) => x.text === 'navigation.clockitgo');
      const clonedGroup = webuserMenu.find((x) => x.text === 'navigation.clockitgo');
      const foundMenu = menuGroup?.submenu?.find((x) => x.route === '/pushmessages');
      if (foundMenu) clonedGroup?.submenu?.push(foundMenu);
    }

    storeTerminalSystems.nav = webuserMenu.find((x) => x.text === 'navigation.masterdata') ?? { icon: '', text: '' };
    return webuserMenu;
  }

  const returnVal = menu.filter((x) => x.route === '/home');
  keycloak.value?.logout();
  return menu.filter((x) => x.route === '/home');
});

const themeText = computed(() => {
  switch (theme.name.value) {
    case 'light':
      return t('menu.light');
    case 'dark':
      return t('menu.dark');
    case 'system':
    default:
      return t('menu.system');
  }
});

const LogoutFunction = async () => {
  await keycloak.value?.logout({ redirectUri: window.location.origin });
};

const LoadAccountInfo = async () => {
  const response = await accInfoApi.GetAccountInfo();
  if (response.status == 200) {
    accInfo.value = response.data;
  }
};

const LoadTerminalSystem = async () => {
  try {
    const response = await terminalSystemApi.GetAllTerminalSystems();
    if (response.status == 200) {
      storeTerminalSystems.data = response.data;
    }
  } catch (err) {
    console.error(err);
  }
};

const openProfile = () => {
  const baseUrl = import.meta.env.VITE_KEYCLOAK_URL;
  const accountUrl = `${baseUrl}/realms/clockitgo/account`;

  window.open(accountUrl, '_blank');
};

const setTerminalNavbar = () => {
  const mdGroup = menu.find((x) => x.text === 'navigation.masterdata') ?? {
    icon: '',
    text: '',
  };
  storeTerminalSystems.data.forEach((x) => {
    switch (x.description) {
      case 'Datafox01':
        const dfMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/datafox') ?? { route: '', text: '' };
        dfMenuItem.isVisible = x.isActive;
        break;
      case 'Intuscom01':
        const iMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/intuscom') ?? { route: '', text: '' };
        iMenuItem.isVisible = x.isActive;
        break;
      case 'Dormakaba01':
        const dMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/dormakaba') ?? { route: '', text: '' };
        dMenuItem.isVisible = x.isActive;
        break;
      case 'CTI01':
        const cMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/cti') ?? { route: '', text: '' };
        cMenuItem.isVisible = x.isActive;
        break;
      case 'Reiner SCT01':
        const rMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/reinersct') ?? { route: '', text: '' };
        rMenuItem.isVisible = x.isActive;
        break;
      case 'Multicom01':
        const mMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/multicom') ?? { route: '', text: '' };
        mMenuItem.isVisible = x.isActive;
        break;
      case 'Suprema01':
        const sMenuItem = mdGroup.submenu?.find((x) => x.route === '/terminal/suprema') ?? { route: '', text: '' };
        sMenuItem.isVisible = x.isActive;
        break;
    }
  });
};

const cycleTheme = () => {
  theme.cycle();
  localStorage.setItem('theme-pref', theme.name.value);
};

onMounted(async () => {
  theme.change(themeSetting.value);
  snackbar.value = true;
  await LoadAccountInfo();
  await LoadTerminalSystem();
});
</script>
<style scoped>
/* Remove the default link underline from the entire item (including icon) */
.custom-list-item {
  text-decoration: none !important;
}

/* Apply underline only to the title text on hover */
.custom-list-item:hover :deep(.v-list-item-title) {
  text-decoration: underline !important;
}

.custom-link {
  color: rgb(var(--v-theme-anchor-color));
  text-decoration: none;
  transition: text-decoration 0.2s ease;
}

.footer-link {
  color: #34eabc;
  text-decoration: none;
  transition: text-decoration 0.2s ease;
}

.custom-link:hover,
.footer-link:hover {
  text-decoration: underline;
}
</style>
